import { NJText } from '@engie-group/fluid-design-system-react';
import { UMIcon } from '../../urbanmap/_components';
import { isValidContract } from './domain';
import type { PerimeterDraft } from './StepPerimeter';
import './creation.css';

export type ProgressState = 'untouched' | 'incomplete' | 'current' | 'success' | 'error' | 'busy';
export interface ProgressItem {
  state: ProgressState;
  detail: string;
}
export type SectionKey = 'name' | 'perimeter' | 'weather' | 'kind' | 'contract';
export type SectionProgress = Record<SectionKey, ProgressItem>;

const STATE_LABEL: Record<ProgressState, string> = {
  untouched: 'À renseigner', incomplete: 'À compléter', current: 'En cours',
  success: 'Validé', error: 'À corriger', busy: 'Copie en cours',
};
const STATE_ICON: Record<ProgressState, string> = {
  untouched: 'radio_button_unchecked', incomplete: 'pending', current: 'edit',
  success: 'check_circle', error: 'error_outline', busy: 'sync',
};

/** The same field checks drive both progress displays and the Next button. */
export function perimeterProgress(
  draft: PerimeterDraft, buildingCount: number, stationValid: boolean,
  touched: Partial<Record<SectionKey, boolean>>,
): SectionProgress {
  const ready = draft.mode === 'communes' ? draft.communes.length > 0 : draft.closed && draft.points.length >= 3;
  return {
    name: draft.name.trim()
      ? { state: 'success', detail: 'Nom renseigné' }
      : { state: touched.name ? 'error' : 'untouched', detail: 'Donnez un nom au projet' },
    perimeter: draft.drawing
      ? { state: 'current', detail: 'Tracé en cours sur la carte' }
      : ready
        ? buildingCount > 0
          ? { state: 'success', detail: `${buildingCount.toLocaleString('fr-FR')} bâtiments inclus` }
          : { state: 'error', detail: 'Aucun bâtiment dans ce périmètre' }
        : draft.mode === 'drawn' && draft.points.length > 0
          ? { state: 'incomplete', detail: 'Validez le contour pour continuer' }
          : { state: touched.perimeter ? 'incomplete' : 'untouched', detail: 'Sélectionnez une commune ou un contour' },
    weather: ready && buildingCount > 0
      ? stationValid
        ? { state: 'success', detail: 'Station de référence retenue' }
        : { state: 'error', detail: 'Choisissez une station météo' }
      : { state: 'untouched', detail: 'En attente du périmètre' },
    kind: draft.kind === 'new' || draft.networkIds.length > 0
      ? { state: 'success', detail: draft.kind === 'new' ? 'Nouveau réseau' : `${draft.networkIds.length} réseau(x) repris` }
      : { state: 'error', detail: 'Sélectionnez au moins un réseau' },
    contract: isValidContract(draft.contract)
      ? { state: 'success', detail: 'Période du contrat valide' }
      : { state: touched.contract ? 'error' : 'incomplete', detail: 'Vérifiez les mois de début et de fin' },
  };
}

export function ProgressMark({ item, number, icon = 'radio_button_unchecked' }: { item: ProgressItem; number?: number; icon?: string }) {
  return (
    <div className="pcv3-progress-mark" data-state={item.state} aria-hidden="true">
      {item.state === 'untouched' && number !== undefined ? <NJText as="span" scale="sm">{number}</NJText>
        : <UMIcon name={item.state === 'untouched' ? icon : STATE_ICON[item.state]} size={20} color="currentColor" />}
    </div>
  );
}

export function ProgressLabel({ item }: { item: ProgressItem }) {
  return <NJText as="span" scale="xs" className="pcv3-progress-label">{STATE_LABEL[item.state]} · {item.detail}</NJText>;
}

const STEPS = ['Projet et périmètre', 'Mes ratios', 'Récapitulatif'];

export function CreationStepper({ current, items }: { current: number; items: ProgressItem[] }) {
  return (
    <nav className="pcv3-creation-progress" aria-label="Progression de la création du projet">
      <div className="pcv3-creation-title">
        <NJText as="strong" scale="md">Création projet</NJText>
        <NJText as="span" scale="xs">Étape {current + 1} sur 3</NJText>
      </div>
      <ol className="pcv3-creation-steps">
        {STEPS.map((title, i) => (
          <li key={title} className="pcv3-creation-step" data-state={items[i].state} aria-current={current === i ? 'step' : undefined} aria-busy={items[i].state === 'busy'}>
            <ProgressMark item={items[i]} number={i + 1} />
            <div className="pcv3-progress-copy" aria-live="polite">
              <NJText as="strong" scale="sm">{title}</NJText>
              <ProgressLabel item={items[i]} />
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
}
